import React from 'react';
import { Link } from 'react-router-dom';
import {
  FiBox,
  FiTrendingUp,
  FiUsers,
  FiShield,
  FiFileText,
  FiCheckCircle,
  FiArrowRight,
} from 'react-icons/fi';
import { Page, LayoutComposer } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './FactoryPartnershipPage.module.css';

const BENEFITS = [
  {
    icon: <FiTrendingUp />,
    title: 'Stable Production Opportunities',
    text: 'Our Order Aggregation Engine combines fragmented buyer demand into predictable, high-volume production cycles that easily fulfill your factory MOQs.',
  },
  {
    icon: <FiUsers />,
    title: 'Zero Foreign Customer Friction',
    text: 'You never have to navigate language barriers or manage dozens of small overseas buyers. All buyer communications, specs, and questions are coordinated by your assigned Sourcing Agent.',
  },
  {
    icon: <FiShield />,
    title: 'Protected Identity & Clear Pricing',
    text: 'Your factory direct contact info is safeguarded. You maintain complete control over tiered quantity pricing, lead times, capacity limits, and packaging requirements.',
  },
];

const ROADMAP_STEPS = [
  {
    step: 'Step 1',
    title: 'Download Product Template',
    text: 'Receive our standardized Excel/CSV product template designed for rapid catalog ingestion and SKU mapping.',
  },
  {
    step: 'Step 2',
    title: 'Fill Product Specifications',
    text: 'Provide SKU, product category, materials, dimensions, MOQ, lead times, packaging data, and tiered factory pricing.',
  },
  {
    step: 'Step 3',
    title: 'Submit & Go Live',
    text: 'ChinaDolphins audits submitted listings. Approved products are published and represented globally by your verified Sourcing Agent.',
  },
];

const FactoryPartnershipPage = () => {
  const layoutAreas = `
    topbar
    main
    footer
  `;

  return (
    <Page
      title="Factory Partnership Program | ChinaDolphins B2B Sourcing"
      description="Partner with ChinaDolphins to unlock high-volume global B2B orders. Combine international demand without foreign customer management headaches."
      className={css.root}
    >
      <LayoutComposer areas={layoutAreas}>
        {props => {
          const { Topbar, Main, Footer } = props;
          return (
            <>
              <Topbar as="header">
                <TopbarContainer />
              </Topbar>

              <Main as="main" id="main-content">
                {/* ── Hero ── */}
                <section className={css.hero}>
                  <div className={css.heroInner}>
                    <div className={css.heroBadge}>
                      <FiBox /> Manufacturing Partner Program
                    </div>
                    <h1 className={css.heroTitle}>
                      Unlock High-Volume Global B2B Orders
                    </h1>
                    <p className={css.heroSubtitle}>
                      ChinaDolphins connects Chinese manufacturing partners with global businesses. Our Order Aggregation Engine pools international demand so you focus 100% on manufacturing while our Agent network manages international discovery and communications.
                    </p>
                    <Link to="/signup/factory" className={css.heroBtn}>
                      Apply for Factory Onboarding <FiArrowRight />
                    </Link>
                  </div>
                </section>

                <section className={css.section}>
                  {/* ── 3 Benefits ── */}
                  <div className={css.sectionHeader}>
                    <div className={css.sectionTag}>Manufacturing Benefits</div>
                    <h2 className={css.sectionTitle}>Why Partner with ChinaDolphins?</h2>
                  </div>

                  <div className={css.benefitGrid}>
                    {BENEFITS.map((b, idx) => (
                      <div key={idx} className={css.benefitCard}>
                        <div className={css.benefitIcon}>{b.icon}</div>
                        <h3 className={css.benefitTitle}>{b.title}</h3>
                        <p className={css.benefitText}>{b.text}</p>
                      </div>
                    ))}
                  </div>

                  {/* ── 3-Step Product Onboarding ── */}
                  <div className={css.roadmapCard}>
                    <div className={css.sectionHeader} style={{ marginBottom: 12 }}>
                      <div className={css.sectionTag}>Streamlined Onboarding</div>
                      <h2 className={css.sectionTitle}>3-Step Product Publishing</h2>
                      <p style={{ color: '#64748b', margin: 0 }}>
                        Get your catalog represented to global buyers in three simple steps.
                      </p>
                    </div>

                    <div className={css.roadmapGrid}>
                      {ROADMAP_STEPS.map((s, idx) => (
                        <div key={idx} className={css.roadmapStep}>
                          <span className={css.stepBadge}>{s.step}</span>
                          <h4 className={css.roadmapTitle}>{s.title}</h4>
                          <p className={css.roadmapText}>{s.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* ── Bottom CTA Box ── */}
                  <div className={css.ctaBox}>
                    <h2 className={css.ctaTitle}>Start Receiving Aggregated International Orders</h2>
                    <p className={css.ctaText}>
                      Join ChinaDolphins today. Register your manufacturing facility to connect with verified Sourcing Agents and global buyers.
                    </p>
                    <Link to="/signup/factory" className={css.heroBtn}>
                      Apply for Factory Registration <FiArrowRight />
                    </Link>
                  </div>
                </section>
              </Main>

              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </Page>
  );
};

export default FactoryPartnershipPage;
